/**
 * Validation utilities for form fields
 */

const BASE_API: string = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api`;

export interface CityInfo {
    postcode: string;
    name: string;
    inseeCode: string;
}

export interface PostcodeApiResponse {
    isValid: boolean;
    reason: string | null;
    cities: CityInfo[];
}

export interface PostcodeValidationResponse {
    valid: boolean;
    city?: string;
    cities?: CityInfo[];
    inseeCode?: string;
    message?: string;
}

/**
 * Validates a French postcode via API
 * @param postcode - The postcode to validate (5 digits)
 * @returns Promise with validation result and location data
 */
export async function validatePostcode(postcode: string): Promise<PostcodeValidationResponse> {
    try {
        // Basic format validation before API call - French postcode regex (01-98, excluding 00 and 99)
        if (!postcode || !/^(?:0[1-9]|[1-8]\d|9[0-8])\d{3}$/.test(postcode)) {
            return {
                valid: false,
                message: 'Le code postal n\'est pas valide'
            };
        }

        const response = await fetch(
            `${BASE_API}/validate/postcode/${postcode}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            return {
                valid: false,
                message: 'Impossible de valider le code postal'
            };
        }

        const data: PostcodeApiResponse = await response.json();
        
        // Check if postcode is invalid
        if (!data.isValid) {
            return {
                valid: false,
                message: data.reason || 'Le code postal n\'est pas valide'
            };
        }

        // Valid postcode - extract city information
        if (data.cities && data.cities.length > 0) {
            // Always pick the first city if multiple exist
            const firstCity = data.cities[0];
            
            return {
                valid: true,
                city: firstCity.name,
                cities: data.cities,
                inseeCode: firstCity.inseeCode
            };
        }

        // Unexpected response format
        return {
            valid: false,
            message: 'Réponse invalide du serveur'
        };
    } catch (error) {
        console.error('Error validating postcode:', error);
        return {
            valid: false,
            message: 'Erreur lors de la validation du code postal'
        };
    }
}

/**
 * Validates a postcode and returns the city name if valid
 * @param postcode - The postcode to validate
 * @returns Promise with city name or null
 */
export async function getCityFromPostcode(postcode: string): Promise<string | null> {
    const result = await validatePostcode(postcode);
    return result.valid && result.city ? result.city : null;
}

